import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Copy, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { RateLimitError, useImpersonate } from '../../hooks/useImpersonate';
import { useUser } from '../../hooks/useUsers';
import { adminApi } from '../../lib/api';
import { formatDateTime } from '../../lib/format';

interface Membership {
  org_id: string;
  org_name: string;
  role: string;
}

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const userId = id ?? '';

  const qc = useQueryClient();
  const { data, isLoading } = useUser(userId);
  const impersonateMutation = useImpersonate();
  const removeMutation = useMutation({
    mutationFn: ({ orgId, uid }: { orgId: string; uid: string }) =>
      adminApi.organizations.removeMember(orgId, uid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', userId] });
    },
  });

  const [removeTarget, setRemoveTarget] = useState<Membership | null>(null);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [impersonateLink, setImpersonateLink] = useState<string | null>(null);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const user = data?.user;
  const memberships: Membership[] = data?.memberships ?? [];

  function handleImpersonateConfirm() {
    setImpersonateError(null);
    impersonateMutation.mutate(userId, {
      onSuccess: (res) => {
        setImpersonateLink(res.link);
      },
      onError: (err) => {
        if (err instanceof RateLimitError) {
          setImpersonateError('Rate limit alcanzado. Puedes impersonar hasta 5 usuarios por hora.');
        } else {
          setImpersonateError((err as Error).message ?? 'Error al generar enlace.');
        }
      },
    });
  }

  function handleCopy() {
    if (!impersonateLink) return;
    void navigator.clipboard.writeText(impersonateLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleImpersonateClose() {
    setShowImpersonate(false);
    setImpersonateLink(null);
    setImpersonateError(null);
    impersonateMutation.reset();
  }

  const memberColumns = [
    {
      header: 'Organización',
      accessor: (m: Membership) => (
        <Link
          to={`/organizations/${m.org_id}`}
          className="text-sm hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {m.org_name}
        </Link>
      ),
    },
    {
      header: 'Rol',
      accessor: (m: Membership) => (
        <Badge variant="secondary">{m.role === 'owner' ? 'Propietario' : 'Miembro'}</Badge>
      ),
    },
    {
      header: '',
      accessor: (m: Membership) => (
        <Button
          size="sm"
          variant="destructive"
          onClick={(e) => {
            e.stopPropagation();
            setRemoveTarget(m);
          }}
        >
          Eliminar
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-primary" />
      </div>
    );
  }

  if (!user) {
    return <p className="text-muted-foreground">Usuario no encontrado.</p>;
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <Link
          to="/users"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft size={14} />
          Usuarios
        </Link>

        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{user.email}</h1>
            <div className="flex items-center gap-2">
              {user.app_metadata.is_admin && <Badge variant="default">Admin</Badge>}
            </div>
          </div>
          <Button variant="outline" onClick={() => setShowImpersonate(true)}>
            Impersonar
          </Button>
        </div>
      </div>

      <section>
        <h2 className="text-base font-semibold mb-3">Metadatos</h2>
        <div className="flex flex-col divide-y divide-border rounded-lg border">
          {[
            { label: 'ID', value: <span className="font-mono text-xs">{user.id}</span> },
            { label: 'Email', value: user.email },
            { label: 'Creado', value: formatDateTime(user.created_at) },
            { label: 'Último acceso', value: formatDateTime(user.last_sign_in_at) },
            {
              label: 'App Metadata',
              value: (
                <pre className="font-mono text-xs whitespace-pre-wrap">
                  {JSON.stringify(user.app_metadata, null, 2)}
                </pre>
              ),
            },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-baseline gap-6 px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{label}</span>
              <span className="text-sm">{value}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold mb-3">Membresías</h2>
        <DataTable
          columns={memberColumns}
          data={memberships}
          emptyMessage="Sin membresías."
          getRowKey={(m) => m.org_id}
        />
      </section>

      {/* Impersonation dialog */}
      <Dialog
        open={showImpersonate}
        onOpenChange={(open) => {
          if (!open) handleImpersonateClose();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonar usuario</DialogTitle>
          </DialogHeader>

          {!impersonateLink ? (
            <div className="space-y-4 py-2">
              <p className="text-sm">
                ¿Generar magic link para <strong>{user.email}</strong>?
              </p>
              <p className="text-xs text-muted-foreground rounded-md border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-yellow-700 dark:text-yellow-400">
                Usa un perfil de navegador separado o ventana incógnito para no perder tu sesión de
                administrador.
              </p>
              {impersonateError && <p className="text-sm text-destructive">{impersonateError}</p>}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Enlace generado. Ábrelo en una ventana incógnito.
              </p>
              <div className="flex gap-2">
                <Input readOnly value={impersonateLink} className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  <Copy size={14} />
                  {copied ? 'Copiado' : 'Copiar'}
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a href={impersonateLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={14} />
                  </a>
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleImpersonateClose}>
              {impersonateLink ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!impersonateLink && (
              <Button onClick={handleImpersonateConfirm} disabled={impersonateMutation.isPending}>
                {impersonateMutation.isPending ? 'Generando...' : 'Generar enlace'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Eliminar membresía"
        description={`¿Eliminar al usuario de "${removeTarget?.org_name}"?`}
        onConfirm={() => {
          if (!removeTarget) return;
          removeMutation.mutate(
            { orgId: removeTarget.org_id, uid: userId },
            { onSuccess: () => setRemoveTarget(null) }
          );
        }}
        confirmLabel="Eliminar"
        isDestructive
        isPending={removeMutation.isPending}
      />
    </div>
  );
}
